import { type CSSProperties, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, Check, X } from 'lucide-react';
import type { View } from '../types';
import { SecretaryGeneral } from './SecretaryGeneral';
import { prefersReducedMotion } from '../browserCompat';

interface TutorialStep {
  target: string;
  view?: View;
  eyebrow: string;
  title: string;
  body: string;
}

const steps: TutorialStep[] = [
  { target: 'command', eyebrow: 'WELCOME TO THE EB OS', title: 'I am your Secretary General', body: 'I will guide you through this Executive Board workspace. The top bar always shows your committee, live connection, refresh control and profile.' },
  { target: 'overview', view: 'overview', eyebrow: '01 · OVERVIEW', title: 'Your committee at a glance', body: 'See live totals, the current committee session, active speaker and recent activity without opening separate screens.' },
  { target: 'delegates', view: 'delegates', eyebrow: '02 · DELEGATES', title: 'The complete delegate roster', body: 'Search delegates by name, portfolio or school, sort the roster and open a delegate’s private EB record.' },
  { target: 'attendance', view: 'attendance', eyebrow: '03 · ATTENDANCE', title: 'Take the committee roll', body: 'Mark Present, Present & Voting, Late or Absent. Updates are shared with the other authorized EB members in real time.' },
  { target: 'speakers', view: 'speakers', eyebrow: '04 · SPEAKERS', title: 'Run the Speakers’ List', body: 'Add delegates, set speaking time, choose the current speaker, pause, skip and drag the queue into the correct order.' },
  { target: 'sessions', view: 'sessions', eyebrow: '05 · SESSIONS', title: 'Control committee activity', body: 'Start the GSL or a caucus, define the topic and duration, pause the timer and end the session when the committee moves on.' },
  { target: 'motions', view: 'motions', eyebrow: '06 · MOTIONS', title: 'Review motions clearly', body: 'Accept, modify, reject or archive submitted motions while preserving the committee’s operational history.' },
  { target: 'requests', view: 'requests', eyebrow: '07 · POINTS & REQUESTS', title: 'Respond to delegates', body: 'Acknowledge pending points, mark them in progress, resolve them or dismiss invalid requests.' },
  { target: 'voting', view: 'voting', eyebrow: '08 · VOTING', title: 'Open and finalize votes', body: 'Configure the threshold and eligibility rules, record each vote and finalize the result when voting is complete.' },
  { target: 'documents', view: 'documents', eyebrow: '09 · DOCUMENTS', title: 'Review committee documents', body: 'Open working papers, draft resolutions and amendments. Save reviews, request changes, approve or archive submissions.' },
  { target: 'scorecard', view: 'scorecard', eyebrow: '10 · LIVE SCORECARD', title: 'Evaluate delegates privately', body: 'Enter criterion scores in the live autosaving scorecard. Conflict protection prevents one EB member from silently overwriting another.' },
  { target: 'evaluation', view: 'evaluation', eyebrow: '11 · EVALUATION', title: 'Check existing evaluation records', body: 'Review weighted legacy evaluation totals while using Live Scorecard for current scoring.' },
  { target: 'awards', view: 'awards', eyebrow: '12 · AWARDS', title: 'Submit recommendations', body: 'Recommend delegates for awards with a rationale. Recommendations go only to the Super Admin and Secretariat.' },
  { target: 'secretariat', view: 'secretariat', eyebrow: '13 · SECRETARIAT', title: 'Escalate securely', body: 'Send academic, technical, delegate, emergency or logistics matters directly to the Secretariat.' },
  { target: 'announcements', view: 'announcements', eyebrow: '14 · ANNOUNCEMENTS', title: 'Publish or read instructions', body: 'Conference-wide and committee-specific announcements appear here live. Chairs and Vice-Chairs can publish to all delegates or one selected committee.' },
  { target: 'history', view: 'history', eyebrow: '15 · HISTORY', title: 'Follow the audit trail', body: 'See the chronological committee record. Important actions remain traceable instead of disappearing.' },
  { target: 'summary', view: 'summary', eyebrow: '16 · FINAL SUMMARY', title: 'Close with verified totals', body: 'Review the final activity and outcome totals produced from the committee’s recorded work.' },
  { target: 'utility', eyebrow: 'LIVE UTILITY', title: 'Keep essential context visible', body: 'The right panel keeps the timer, current speaker, pending requests and committee status beside every workspace module.' },
  { target: 'music', eyebrow: 'MUSIC PLAYER', title: 'Control your workspace audio', body: 'Choose a track, pause or continue playback, move through the playlist and control volume without covering committee controls.' },
  { target: 'settings', view: 'settings', eyebrow: '17 · SETTINGS', title: 'Your profile and tutorial controls', body: 'Update your public EB profile here. Your committee assignment remains protected and controlled by the Super Admin.' },
  { target: 'tutorial-replay', view: 'settings', eyebrow: 'TOUR COMPLETE', title: 'You can call me back anytime', body: 'Use Replay Secretary General tutorial in Settings whenever you need a refresher. If anything is unclear, contact the developer or Secretariat.' },
];

interface Spotlight {
  left: number;
  top: number;
  width: number;
  height: number;
}

interface Position {
  left: number;
  top: number;
  direction: 'left' | 'right';
}

const clamp = (value: number, minimum: number, maximum: number) => Math.min(Math.max(value, minimum), maximum);

export function GuardianTutorial({ onSelectView, onComplete }: { onSelectView: (view: View) => void; onComplete: () => void }) {
  const [stepIndex, setStepIndex] = useState(0);
  const [spotlight, setSpotlight] = useState<Spotlight | null>(null);
  const [position, setPosition] = useState<Position>({ left: 320, top: 120, direction: 'left' });
  const [departing, setDeparting] = useState(false);
  const nextRef = useRef<HTMLButtonElement>(null);
  const step = steps[stepIndex];

  const measure = () => {
    const target = document.querySelector<HTMLElement>(`[data-tour="${step.target}"]`);
    if (!target) { setSpotlight(null); return; }
    target.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    const rect = target.getBoundingClientRect();
    const padding = step.target === 'command' || step.target === 'utility' ? 8 : 7;
    setSpotlight({ left: rect.left - padding, top: rect.top - padding, width: rect.width + padding * 2, height: rect.height + padding * 2 });
    const panelWidth = Math.min(430, innerWidth - 48);
    const panelHeight = 350;
    const targetCenter = rect.left + rect.width / 2;
    if (targetCenter < innerWidth / 2) {
      setPosition({ left: clamp(rect.right + 24, 24, innerWidth - panelWidth - 24), top: clamp(rect.top + rect.height / 2 - panelHeight / 2, 24, innerHeight - panelHeight - 24), direction: 'left' });
    } else {
      setPosition({ left: clamp(rect.left - panelWidth - 24, 24, innerWidth - panelWidth - 24), top: clamp(rect.top + rect.height / 2 - panelHeight / 2, 24, innerHeight - panelHeight - 24), direction: 'right' });
    }
  };

  useLayoutEffect(() => {
    const frame = requestAnimationFrame(measure);
    const delayed = window.setTimeout(measure, 180);
    addEventListener('resize', measure);
    return () => { cancelAnimationFrame(frame); clearTimeout(delayed); removeEventListener('resize', measure); };
  }, [stepIndex, step.target]);

  useEffect(() => { nextRef.current?.focus({ preventScroll: true }); }, [stepIndex]);
  useEffect(() => {
    const keyboard = (event: KeyboardEvent) => {
      if (event.key === 'Escape') finish();
      if (event.key === 'ArrowRight' || event.key === 'Enter') { event.preventDefault(); advance(); }
      if (event.key === 'ArrowLeft' && stepIndex > 0) { event.preventDefault(); moveTo(stepIndex - 1); }
    };
    addEventListener('keydown', keyboard);
    return () => removeEventListener('keydown', keyboard);
  }, [stepIndex]);

  const moveTo = (index: number) => {
    const next = steps[index];
    if (next.view) onSelectView(next.view);
    setStepIndex(index);
  };
  const advance = () => stepIndex === steps.length - 1 ? finish() : moveTo(stepIndex + 1);
  const finish = () => {
    if (departing) return;
    setDeparting(true);
    window.setTimeout(onComplete, prefersReducedMotion() ? 80 : 1050);
  };

  const guideStyle = { left: position.left, top: position.top } as CSSProperties;
  const eyeStyle = { '--eye-x': position.direction === 'left' ? '-3px' : '3px', '--eye-y': '1px' } as CSSProperties;
  return <div className={`guardian-tour ${departing ? 'departing' : ''}`} role="dialog" aria-modal="true" aria-labelledby="guardian-tour-title">
    {spotlight && <div className="guardian-spotlight" style={spotlight} aria-hidden="true" />}
    <section className={`guardian-tour-card points-${position.direction}`} style={guideStyle}>
      <button className="guardian-tour-close" aria-label="Skip Secretary General tutorial" onClick={finish}><X /></button>
      <div className="guardian-tour-character" aria-hidden="true">
        <span className="guardian-aura" />
        <SecretaryGeneral className={`tutorial-guide points-${position.direction}`} style={eyeStyle} />
      </div>
      <div className="guardian-tour-copy">
        <p className="eyebrow">{step.eyebrow}</p>
        <h2 id="guardian-tour-title">{step.title}</h2>
        <p>{step.body}</p>
        <div className="guardian-tour-progress"><i style={{ width: `${((stepIndex + 1) / steps.length) * 100}%` }} /><span>{stepIndex + 1} / {steps.length}</span></div>
        <div className="guardian-tour-actions">
          <button className="tutorial-skip" onClick={finish}>{stepIndex === steps.length - 1 ? 'Finish' : 'Skip tour'}</button>
          {stepIndex > 0 && <button className="tutorial-back" onClick={() => moveTo(stepIndex - 1)}><ArrowLeft /> Back</button>}
          <button ref={nextRef} className="tutorial-next" onClick={advance}>{stepIndex === steps.length - 1 ? <><Check /> Finish tour</> : <>Next <ArrowRight /></>}</button>
        </div>
      </div>
    </section>
  </div>;
}

export const guardianTutorialStepCount = steps.length;
